import classNames from 'classnames'
import { motion } from 'framer-motion'

import { commonIcons } from '@apps-common/config/icons'
import { ThemeMode } from '@apps-common/config/theme'
import { useTheme } from '@apps-common/hooks/useTheme'
import { useThemeStore } from '@apps-common/stores/useThemeStore'

interface IToggleWidgetProps {
  containerClasses?: string
}

export const ThemeModeToggleWidget: React.FC<IToggleWidgetProps> = ({ containerClasses }) => {
  const { isDarkMode } = useTheme()
  const { themeMode, setThemeMode } = useThemeStore()

  const LightIcon = commonIcons.themeLight
  const DarkIcon = commonIcons.themeDark

  const getIsLight = () => themeMode === ThemeMode.light

  const getIsDark = () => themeMode === ThemeMode.dark

  return (
    <div
      className={classNames(
        containerClasses,
        'flex flex-col items-center justify-start gap-[5px] bg-surface-2 px-[4px] py-[4px] rounded-[15px] w-[65px]'
      )}
    >
      <div
        className={classNames(
          'relative flex items-center justify-center bg-transparent border-[1px] border-secondary-content h-[24px] w-full rounded-[15px]',
          themeMode === ThemeMode.system && 'opacity-40'
        )}
      >
        <motion.div
          key='theme-toggle-background'
          animate={{
            left: getIsLight() ? '0px' : '28px',
            transition: {
              ease: 'easeInOut',
              duration: 0.15
            }
          }}
          className='absolute bg-surface-2 w-[24px] h-[17px] rounded-[15px] mx-[2px] my-[2px] z-[8]'
        />
        <motion.button
          type='button'
          key='theme-light-button'
          aria-label='Theme light'
          animate={{
            // color: getIsLight() ? 'hsl(var(--s))' : 'hsl(var(--sc))',
            transition: {
              delay: 0.1
            }
          }}
          onClick={() => setThemeMode(ThemeMode.light)}
          className='relative w-[26px] h-[26px] flex items-center justify-center z-[10]'
        >
          <LightIcon className='relative left-[-1px] text-[14px]' />
        </motion.button>
        <motion.button
          key='theme-dark-button'
          type='button'
          aria-label='Theme dark'
          animate={{
            // color: getIsDark() ? 'hsl(var(--s))' : 'hsl(var(--sc))',
            transition: {
              delay: 0.1
            }
          }}
          onClick={() => setThemeMode(ThemeMode.dark)}
          className='relative w-[26px] h-[26px] flex items-center justify-center z-[10]'
        >
          <DarkIcon className='text-[11.5px] ml-[3px]' />
        </motion.button>
      </div>
      <motion.button
        key='theme-auto-button'
        type='button'
        aria-label='Theme auto'
        animate={{
          color: themeMode === ThemeMode.system ? 'hsl(var(--s))' : 'hsl(var(--sc))',
          backgroundColor: themeMode === ThemeMode.system ? 'hsl(var(--sc))' : 'transparent',
          transition: {
            duration: 5
          }
        }}
        onClick={() => setThemeMode(ThemeMode.system)}
        className={classNames(
          'w-full px-[10px] h-[14px] rounded-[15px] flex items-center justify-center bg-transparent text-secondary-content border-[1px] border-secondary-content'
          // themeMode !== ThemeMode.system && 'opacity-40'
        )}
      >
        <span className='uppercase jost-medium tracking-[-0.085em] text-[0.7em]'>auto</span>
      </motion.button>
    </div>
  )
}

interface IProps {
  hideAuto?: boolean
}

export const ThemeModeToggle: React.FC<IProps> = ({ hideAuto }) => {
  const { isDarkMode } = useTheme()
  const { themeMode, setThemeMode } = useThemeStore()

  const LightIcon = commonIcons.themeLight
  const DarkIcon = commonIcons.themeDark

  const getIsLight = () => {
    if (themeMode === ThemeMode.system) {
      return !isDarkMode
    }

    return themeMode === ThemeMode.light
  }

  const getIsDark = () => {
    if (themeMode === ThemeMode.system) {
      return isDarkMode
    }

    return themeMode === ThemeMode.dark
  }

  return (
    <div className={classNames('flex items-center justify-start gap-[5px] rounded-full')}>
      <div
        className={classNames(
          'relative flex items-center justify-center bg-surface-2 border border-border h-[30px] w-[60px] rounded-[15px]'
          // themeMode === ThemeMode.system && 'opacity-40'
        )}
      >
        {themeMode !== ThemeMode.system && (
          <motion.div
            key='theme-toggle-background'
            animate={{
              left: getIsLight() ? '0px' : '30px',
              transition: {
                ease: 'easeInOut',
                duration: 0.15
              }
            }}
            className='absolute bg-surface w-[24px] h-[24px] rounded-[15px] mx-[2px] my-[2px] z-[8]'
          />
        )}
        <motion.button
          type='button'
          key='theme-light-button'
          aria-label='Theme light'
          animate={{
            // color: getIsLight() ? 'hsl(var(--rn-surface-2))' : 'hsl(var(--rn-text))',
            transition: {
              delay: 0.1
            }
          }}
          onClick={() => setThemeMode(ThemeMode.light)}
          className='relative w-[26px] h-[26px] flex items-center justify-center z-[10] text-text'
        >
          <LightIcon className='relative left-[-2px] text-[16px]' />
        </motion.button>
        <motion.button
          key='theme-dark-button'
          type='button'
          aria-label='Theme dark'
          animate={{
            // color: getIsDark() ? 'hsl(var(--s))' : 'hsl(var(--sc))',
            transition: {
              delay: 0.1
            }
          }}
          onClick={() => setThemeMode(ThemeMode.dark)}
          className='relative w-[26px] h-[26px] flex items-center justify-center z-[10] text-text'
        >
          <DarkIcon className='text-[13.5px] ml-[2px]' />
        </motion.button>
      </div>
      <motion.button
        key='theme-auto-button'
        type='button'
        aria-label='Theme auto'
        animate={{
          // color: themeMode === ThemeMode.system ? 'hsl(var(--s))' : 'hsl(var(--sc))',
          // backgroundColor: themeMode === ThemeMode.system ? 'hsl(var(--sc))' : 'hsl(var(--s))',
          transition: {
            duration: 5
          }
        }}
        onClick={() => setThemeMode(ThemeMode.system)}
        className={classNames('px-[10px] h-[30px] rounded-[15px] flex items-center justify-center text-text border-[1px] border-border')}
      >
        <span
          className={classNames('uppercase jost-medium tracking-[-0.085em] text-[0.7em]', themeMode === ThemeMode.system && 'bg-surface')}
        >
          system
        </span>
      </motion.button>
    </div>
  )
}
